import { useCallback } from 'react';

import { generatePath } from 'react-router-dom';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';

import { SERVICE_ROUTES } from '#client/constants/serviceRoutes';

import { useDashboardPath } from './useDashboardPath';

const applyTemplate = (basePath, businessEntityUuid) =>
  basePath.includes(':businessEntityUuid') ? generatePath(basePath, { businessEntityUuid }) : basePath;

/*
 * Builds an absolute URL for an `external` entry, replicating the query-param logic
 * the product-CTA and checkout builders shared. `idAliases` (array) and `idAlias` (single)
 * map to the entity id; `orderIdAlias` maps to the order id; every other option key
 * is a literal query param. The basePath's own query string is preserved when the
 * entry contributes no params (e.g. affiliate links like `?ref=6743`).
 */
const buildExternalUrl = (path, options, businessEntityUuid, orderUuid, searchParamDelimiter) => {
  const { idAliases, idAlias, orderIdAlias, ...data } = options || {};
  const url = new URL(path, window.location);
  const searchParams = new URLSearchParams();
  let hasParams = false;

  Object.entries(data).forEach(([key, value]) => {
    searchParams.set(key, value);
    hasParams = true;
  });

  if (Array.isArray(idAliases)) {
    idAliases.forEach((alias) => searchParams.set(alias, businessEntityUuid));
    hasParams = true;
  }

  if (orderIdAlias && orderUuid) {
    searchParams.set(orderIdAlias, orderUuid);
    hasParams = true;
  }

  if (idAlias) {
    searchParams.set(idAlias, businessEntityUuid);
    hasParams = true;
  }

  if (hasParams) {
    url.search = searchParams.toString();
  }

  if (searchParamDelimiter) {
    url.search = url.search.replace(/&/g, searchParamDelimiter);
  }

  return decodeURIComponent(String(url));
};

/**
 * The single canonical URL/redirect resolver. Reads the service-code config in
 * `serviceRoutes.js` and resolves a destination for any context, replacing the five
 * ad-hoc builders that used to drift apart.
 *
 * Returns a `resolve` function. `resolve(serviceCode, opts)` returns
 * `{ url, forceReload, found }` and accepts opts:
 *   - context: 'product_cta' | 'checkout_page' | 'checkout_slide' | 'enterprise' (default 'product_cta')
 *   - orderUuid: order id for entries with an `orderIdAlias`
 *   - jurisdictionCode: when set, `journey` entries insert a `/:jurisdiction/` segment
 *       before the uuid (lowercased), mirroring the enterprise builder
 *   - businessEntityUuid: id used for `:businessEntityUuid` templates and external
 *       query-param aliases; defaults to `useBusinessEntity()`. NOTE: `dashboard`-kind
 *       URLs take their `${PARENT_SEGMENT}/{uuid}` prefix from `useDashboardPath()`
 *       (the active business entity), matching the original builders — so this override
 *       affects the template/query portion, not the dashboard prefix. (Consistent today:
 *       no dashboard entry uses the `:businessEntityUuid` template.)
 *   - flags: service feature-flag map (the checkout wrapper passes these)
 *
 * It never throws and never returns null — callers project their own not-found
 * behavior (throw / null / '' / fallback) off `found`. Feature flags are supplied
 * by the caller rather than read here, so the resolver carries no experiment/router
 * dependency and is safe to call from every context.
 *
 * CONTRACT: `found` means a config entry matched — NOT that a usable URL was produced.
 * A matched entry can still return `url: ''` (e.g. a `journey` entry resolved without a
 * businessEntityUuid, mirroring the enterprise builder's empty string). Callers must
 * treat an empty `url` as non-navigable regardless of `found`.
 *
 * @returns {Function} the memoized `resolve` function
 */
export function useUrlResolver() {
  // `|| {}` only guards this hook's own read, for callers that pass
  // `businessEntityUuid` explicitly (e.g. checkout, whose test mocks
  // `useBusinessEntity` to return undefined). It does NOT make the hook
  // provider-free — `useDashboardPath` still requires the business-entity
  // (and env) providers.
  const { businessEntityUuid: entityUuid } = useBusinessEntity() || {};
  const dashboardPath = useDashboardPath();

  const resolve = useCallback(
    (serviceCode, opts = {}) => {
      const {
        context = 'product_cta',
        orderUuid = null,
        businessEntityUuid = entityUuid,
        flags = {},
        jurisdictionCode,
      } = opts;
      const entry = (SERVICE_ROUTES[context] || {})[serviceCode];

      if (!entry) {
        return { url: '', forceReload: false, found: false };
      }

      const { kind, basePath, options, searchParamDelimiter, forceReload = false } = entry;

      if (kind === 'current') {
        // Deliberately reads window.location rather than useLocation() so the
        // resolver needs no Router context; preserves SlideCheckout's behavior.
        return { url: window.location.pathname, forceReload, found: true };
      }

      const isFlagEnabled = flags[serviceCode] || false;
      const resolvedBasePath = typeof basePath === 'function' ? basePath(businessEntityUuid, isFlagEnabled) : basePath;

      if (kind === 'journey') {
        if (!businessEntityUuid) {
          return { url: '', forceReload, found: true };
        }
        // Mirrors the enterprise builder: when a jurisdiction is known, insert a
        // `/:jurisdiction/` segment before the uuid so journeys-ui can resolve the
        // right config without depending on domesticJurisdiction.
        const template = jurisdictionCode
          ? resolvedBasePath.replace('/:businessEntityUuid', '/:jurisdiction/:businessEntityUuid')
          : resolvedBasePath;
        const params = jurisdictionCode
          ? { jurisdiction: jurisdictionCode.toLowerCase(), businessEntityUuid }
          : { businessEntityUuid };
        return { url: generatePath(template, params), forceReload, found: true };
      }

      if (kind === 'external') {
        // generatePath throws on a missing `:businessEntityUuid` param; guard it
        // like the journey branch so the resolver upholds its never-throws contract.
        if (resolvedBasePath.includes(':businessEntityUuid') && !businessEntityUuid) {
          return { url: '', forceReload, found: true };
        }
        const path = applyTemplate(resolvedBasePath, businessEntityUuid);
        const url = buildExternalUrl(path, options, businessEntityUuid, orderUuid, searchParamDelimiter);
        return { url, forceReload, found: true };
      }

      // kind === 'dashboard'
      const suffix = applyTemplate(resolvedBasePath, businessEntityUuid);
      return { url: `${dashboardPath}${suffix}`, forceReload, found: true };
    },
    [entityUuid, dashboardPath],
  );

  return resolve;
}
